import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { createShop, getShops } from '@/api/shops';
import OpenSlotAutoGenerateModal from './OpenSlotModal';

type ShopItem = {
  id: number;
  shopName: string;
  shopCode?: string | null;
  reprName?: string | null;
  taxId?: string | null;
  activeYn: number;
  sortOrder: number;
  creationTime?: string;

  shopManagerId?: number | null;
  masterUserId?: number | null;
  isMasterYn?: number | null;

  loginId?: string | null;
  nickname?: string | null;
  userName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
};

type ShopListResponse = {
  status: number;
  message: string;
  data: {
    list: ShopItem[];
    totalCount: number;
    page: number;
    rows: number;
  };
};

type SearchFormValues = {
  keyword?: string;
  activeYn?: number;
};

type CreateShopFormValues = {
  shopName: string;
  shopCode?: string;
  reprName?: string;
  taxId?: string;
  activeYn?: number;
  sortOrder?: number;

  loginId: string;
  password: string;
  nickname: string;
  userName: string;
  email?: string;
  phoneNumber?: string;
};

export default function ShopListPage() {
  const [searchForm] = Form.useForm<SearchFormValues>();
  const [createForm] = Form.useForm<CreateShopFormValues>();

  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [autoGenModalShopId, setAutoGenModalShopId] = useState(null)
  const [list, setList] = useState<ShopItem[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  const fetchShops = async ({
    page = 1,
    rows = 20,
  }: {
    page?: number;
    rows?: number;
  } = {}) => {
    try {
      setLoading(true);

      const values = searchForm.getFieldsValue();

      const result = (await getShops({
        keyword: values.keyword,
        activeYn: values.activeYn,
        page,
        rows,
      })) as ShopListResponse;

      setList(result?.data?.list || []);
      setPagination({
        current: page,
        pageSize: rows,
        total: Number(result?.data?.totalCount || 0),
      });
    } catch (error: any) {
      message.error(error?.message || '샵 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops({
      page: 1,
      rows: 20,
    });
  }, []);

  const handleSearch = async () => {
    await fetchShops({
      page: 1,
      rows: pagination.pageSize,
    });
  };

  const handleReset = async () => {
    searchForm.resetFields();

    await fetchShops({
      page: 1,
      rows: pagination.pageSize,
    });
  };

  const handleTableChange = async (nextPagination: TablePaginationConfig) => {
    await fetchShops({
      page: nextPagination.current || 1,
      rows: nextPagination.pageSize || 20,
    });
  };

  const handleOpenCreateModal = () => {
    createForm.resetFields();
    createForm.setFieldsValue({
      activeYn: 1,
      sortOrder: 1,
    });
    setOpenCreateModal(true);
  };

  const handleCreateShop = async () => {
    try {
      const values = await createForm.validateFields();

      setCreateLoading(true);

      await createShop({
        shop: {
          shopName: values.shopName.trim(),
          shopCode: values.shopCode?.trim() || undefined,
          reprName: values.reprName?.trim() || undefined,
          taxId: values.taxId?.trim() || undefined,
          activeYn: values.activeYn ?? 1,
          sortOrder: values.sortOrder ?? 1,
        },
        user: {
          loginId: values.loginId.trim(),
          password: values.password,
          nickname: values.nickname.trim(),
          userName: values.userName.trim(),
          email: values.email?.trim() || undefined,
          phoneNumber: values.phoneNumber?.trim() || undefined,
        }
      });

      message.success('샵과 마스터 계정이 생성되었습니다.');
      setOpenCreateModal(false);
      createForm.resetFields();

      await fetchShops({
        page: 1,
        rows: pagination.pageSize,
      });
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }

      message.error(error?.message || '샵 생성 중 오류가 발생했습니다.');
    } finally {
      setCreateLoading(false);
    }
  };

  const columns: ColumnsType<ShopItem> = [
    {
      title: '샵 ID',
      dataIndex: 'id',
      width: 90,
    },
    {
      title: '업체명',
      dataIndex: 'shopName',
      width: 180,
    },
    {
      title: '업체코드',
      dataIndex: 'shopCode',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: '대표자명',
      dataIndex: 'reprName',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: '사업자번호',
      dataIndex: 'taxId',
      width: 160,
      render: (value) => value || '-',
    },
    {
      title: '마스터 이름',
      dataIndex: 'userName',
      width: 140,
      render: (value) => value || '-',
    },
    {
      title: '로그인 ID',
      dataIndex: 'loginId',
      width: 160,
      render: (value) => value || '-',
    },
    {
      title: '연락처',
      dataIndex: 'phoneNumber',
      width: 150,
      render: (value) => value || '-',
    },
    {
      title: '상태',
      dataIndex: 'activeYn',
      width: 100,
      render: (value: number) =>
        Number(value) === 1 ? <Tag color="green">활성</Tag> : <Tag>비활성</Tag>,
    },
    {
      title: '생성일',
      dataIndex: 'creationTime',
      width: 180,
      render: (value) => value || '-',
    },
    {
      title: '액션',
      dataIndex: 'action',
      width: 180,
      render: (value, record) => {
        return (
          <Button onClick={() => setAutoGenModalShopId(record.id)}>슬롯생성기</Button>
        )
      },
    },
  ];
  /**
 * 🔥 여기 핵심: 5글자 loginId 고정 매핑
 */


  // /**
  //  * 🚀 한 번에 실행하는 함수
  //  */
  // async function createAllShops() {
  //   const shopList = [
  //   { name: '렌느브라이덜', loginId: 'renne' },
  //   { name: '브라이드영', loginId: 'bryng' },
  //   { name: '더케네스블랑', loginId: 'kenbl' },
  //   { name: '위드브라이드', loginId: 'wibra' },
  //   { name: '셀레네웨딩', loginId: 'selwd' },
  //   { name: '케이트린', loginId: 'ketrn' },
  //   { name: '우아르', loginId: 'woare' },
  //   { name: '에델린드레스', loginId: 'edlin' },
  //   { name: '제이스포사', loginId: 'jspoa' },
  //   { name: '조슈아벨', loginId: 'josbl' },
  //   { name: '마리에드오늘', loginId: 'maron' },
  //   { name: '모니카블랑쉬', loginId: 'monbl' },
  //   { name: '셀린아뜰리에', loginId: 'selat' },
  //   { name: '디케', loginId: 'dikee' },
  //   { name: '라벨르엘린', loginId: 'labln' },
  //   { name: '안나스포사', loginId: 'annsp' },
  //   { name: '브라이드벨라', loginId: 'brbel' },
  //   { name: '아뜨레블랑', loginId: 'atbln' },
  //   { name: '브라이드k', loginId: 'bridk' },
  //   { name: '더에이미', loginId: 'amyyy' },
  // ];
  //   for (let i = 0; i < shopList.length; i++) {
  //     const item = shopList[i];

  //     const payload = {
  //       shop: {
  //         shopName: item.name,
  //         shopCode: `SHOP${String(i + 1).padStart(3, '0')}`,
  //         reprName: item.name,
  //         activeYn: 1,
  //         sortOrder: i + 1,
  //       },
  //       user: {
  //         loginId: item.loginId,
  //         password: '1234',
  //         nickname: item.name,
  //         userName: item.name,
  //       },
  //     };

  //     try {
  //       await createShop(payload);
  //       console.log(`✅ 생성 완료: ${item.name}`);
  //     } catch (err) {
  //       console.error(`❌ 실패: ${item.name}`, err);
  //     }
  //   }
  // }
  return (
    <>
      <Card
        title="드레스샵 관리"
        extra={
          <Button type="primary" onClick={handleOpenCreateModal}>
            드레스샵 등록
          </Button>
        }
      >
        <Form form={searchForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="keyword">
            <Input
              allowClear
              placeholder="업체명 / 업체코드 / 담당자 / 로그인ID 검색"
              style={{ width: 280 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>

          <Form.Item name="activeYn">
            <Select
              allowClear
              placeholder="활성 여부"
              style={{ width: 140 }}
              options={[
                { label: '활성', value: 1 },
                { label: '비활성', value: 0 },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                검색
              </Button>
              <Button onClick={handleReset}>초기화</Button>
            </Space>
          </Form.Item>
        </Form>

        <Table<ShopItem>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={list}
          scroll={{ x: 1500 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>

      <Modal
        title="드레스샵 등록"
        open={openCreateModal}
        onCancel={() => setOpenCreateModal(false)}
        onOk={handleCreateShop}
        okText="등록"
        cancelText="취소"
        confirmLoading={createLoading}
        width={720}
        destroyOnHidden
      >
        <Form<CreateShopFormValues>
          form={createForm}
          layout="vertical"
          initialValues={{
            activeYn: 1,
            sortOrder: 1,
          }}
        >
          <Card size="small" title="샵 정보" style={{ marginBottom: 16 }}>
            <Form.Item
              label="업체명"
              name="shopName"
              rules={[{ required: true, message: '업체명을 입력해주세요.' }]}
            >
              <Input placeholder="업체명 입력" />
            </Form.Item>

            <Form.Item label="업체코드" name="shopCode">
              <Input placeholder="업체코드 입력" />
            </Form.Item>

            <Form.Item label="대표자명" name="reprName">
              <Input placeholder="대표자명 입력" />
            </Form.Item>

            <Form.Item label="사업자번호" name="taxId">
              <Input placeholder="사업자번호 입력" />
            </Form.Item>

            <Form.Item label="정렬순서" name="sortOrder">
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="활성 여부" name="activeYn">
              <Select
                options={[
                  { label: '활성', value: 1 },
                  { label: '비활성', value: 0 },
                ]}
              />
            </Form.Item>
          </Card>

          <Card size="small" title="마스터 계정 정보">
            <Form.Item
              label="로그인 ID"
              name="loginId"
              rules={[
                { required: true, message: '로그인 ID를 입력해주세요.' },
                { min: 4, message: '로그인 ID는 4자 이상 입력해주세요.' },
              ]}
            >
              <Input placeholder="로그인 ID 입력" />
            </Form.Item>

            <Form.Item
              label="비밀번호"
              name="password"
              rules={[
                { required: true, message: '비밀번호를 입력해주세요.' },
                { min: 4, message: '비밀번호는 4자 이상 입력해주세요.' },
              ]}
            >
              <Input.Password placeholder="비밀번호 입력" />
            </Form.Item>

            <Form.Item
              label="이름"
              name="userName"
              rules={[{ required: true, message: '이름을 입력해주세요.' }]}
            >
              <Input placeholder="이름 입력" />
            </Form.Item>

            <Form.Item
              label="닉네임"
              name="nickname"
              rules={[{ required: true, message: '닉네임을 입력해주세요.' }]}
            >
              <Input placeholder="닉네임 입력" />
            </Form.Item>

            <Form.Item
              label="이메일"
              name="email"
              rules={[{ type: 'email', message: '올바른 이메일 형식이 아닙니다.' }]}
            >
              <Input placeholder="이메일 입력" />
            </Form.Item>

            <Form.Item label="연락처" name="phoneNumber">
              <Input placeholder="연락처 입력" />
            </Form.Item>
          </Card>
        </Form>
      </Modal>
      {
        !!autoGenModalShopId && (
          <OpenSlotAutoGenerateModal shopId={autoGenModalShopId} open={true} onClose={() => setAutoGenModalShopId(false)} />
        )
      }
    </>
  );
}